import React, { FunctionComponent, Context, createContext, useContext, useMemo, } from 'react';

import {IWorkspace, WorkspaceConfigShowCreateOptions} from '../models';

export enum WorkspaceConfig {
  FROM_EMAIL_ADDRESS = 'from_email_address',
  FROM_EMAIL_SENDER = 'from_email_sender',
  S3_BUCKET_NAME = 's3_bucket_name',
  S3_BUCKET_KEY = 's3_bucket_key',
  S3_BUCKET_SECRET = 's3_bucket_secret',
  S3_BUCKET_REGION = 's3_bucket_region',
  S3_BUCKET_PRESIGNED_URL_EXPIRATION = 's3_bucket_presigned_url_expiration',
  DATA_RETENTION_DAYS = 'data_retention_days',
  SHOW_CREATE_OPTIONS = 'show_create_options', // report: boolean, notebook: boolean, tableau_embed: boolean
  CUSTOM_LINKS = 'custom_links',
  CUSTOM_MESSAGE_TITLE = 'custom_message_title',
  CUSTOM_MESSAGE_TEXT = 'custom_message_text',
  CUSTOM_MESSAGE_INTENT = 'custom_message_intent',
  CUSTOM_MESSAGE_ICON = 'custom_message_icon',
  CUSTOM_MESSAGE_ACTIVE = 'custom_message_active',
}

export enum WorkspaceConfigType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'bool',
  ARRAY = 'array',
  DICT = 'dict',
}

export const DEFAULT_SHOW_CREATE_OPTIONS: WorkspaceConfigShowCreateOptions = {
  report: true,
  notebook: true,
  tableau_embed: true,
}

interface IWorkspaceConfigContextContent {
  enableNoteflow: boolean;
  workspace?: IWorkspace | null;
  fromEmailAddress: string;
  fromEmailSender: string;
  s3BucketName: string;
  s3BucketKey: string;
  s3BucketSecret: string;
  s3BucketRegion: string;
  s3BucketPresignedUrlExpiration: number;
  dataRetentionDays: number;
  customLinks: any[];
  customMessageTitle: string;
  customMessageText: string;
  customMessageIntent: string;
  customMessageIcon: string;
  customMessageActive: string;
  showCreateOptions?: WorkspaceConfigShowCreateOptions;
}

interface IWorkspaceConfigContextProviderProps {
  workspace?: IWorkspace | null;
  children: any;
}

const WorkspaceConfigContext: Context<IWorkspaceConfigContextContent> =
  createContext<IWorkspaceConfigContextContent>({
    enableNoteflow: true,
    fromEmailAddress: '',
    fromEmailSender: '',
    s3BucketName: '',
    s3BucketKey: '',
    s3BucketSecret: '',
    s3BucketRegion: '',
    s3BucketPresignedUrlExpiration: 168, // 168 hours
    dataRetentionDays: 30,
    customLinks: [],
    customMessageTitle: '',
    customMessageText: '',
    customMessageIntent: '',
    customMessageIcon: '',
    customMessageActive: 'false',
    showCreateOptions: DEFAULT_SHOW_CREATE_OPTIONS
  });

export const WorkspaceConfigContextProvider: FunctionComponent<IWorkspaceConfigContextProviderProps> = ({
  workspace,
  children
}: IWorkspaceConfigContextProviderProps) => {
  const enableNoteflow = workspace?.config_json?.enable_noteflow ?? true;
  const fromEmailAddress = workspace?.config_json?.from_email_address ?? 'do-not-reply@pushmetrics.io';
  const fromEmailSender = workspace?.config_json?.from_email_sender ?? '';
  const s3BucketName = workspace?.config_json?.s3_bucket_name ?? '';
  const s3BucketKey = workspace?.config_json?.s3_bucket_key ?? '';
  const s3BucketSecret = workspace?.config_json?.s3_bucket_secret ?? '';
  const s3BucketRegion = workspace?.config_json?.s3_bucket_region ?? '';
  const s3BucketPresignedUrlExpiration = workspace?.config_json?.s3_bucket_presigned_url_expiration ?? 168;
  const dataRetentionDays = workspace?.config_json?.data_retention_days ?? 30;
  const showCreateOptions: WorkspaceConfigShowCreateOptions =
    workspace?.config_json?.show_create_options ?? DEFAULT_SHOW_CREATE_OPTIONS;
  const customLinks = workspace?.config_json?.custom_links ?? [];
  const customMessageTitle = workspace?.config_json?.custom_message_title ?? '';
  const customMessageText = workspace?.config_json?.custom_message_text ?? '';
  const customMessageIntent = workspace?.config_json?.custom_message_intent ?? '';
  const customMessageIcon = workspace?.config_json?.custom_message_icon ?? '';
  const customMessageActive = workspace?.config_json?.custom_message_active ?? 'false';

  const value = useMemo(() => ({
    enableNoteflow: enableNoteflow,
    workspace: workspace,
    fromEmailAddress: fromEmailAddress,
    fromEmailSender: fromEmailSender,
    s3BucketName: s3BucketName,
    s3BucketKey: s3BucketKey,
    s3BucketSecret: s3BucketSecret,
    s3BucketRegion: s3BucketRegion,
    s3BucketPresignedUrlExpiration: s3BucketPresignedUrlExpiration,
    dataRetentionDays: dataRetentionDays,
    customLinks: customLinks,
    customMessageTitle: customMessageTitle,
    customMessageText: customMessageText,
    customMessageIntent: customMessageIntent,
    customMessageIcon: customMessageIcon,
    customMessageActive: customMessageActive,
    showCreateOptions: showCreateOptions,
  }), [ enableNoteflow, workspace, fromEmailAddress, fromEmailSender, s3BucketKey, s3BucketName, s3BucketSecret,
    s3BucketRegion, s3BucketPresignedUrlExpiration, dataRetentionDays, customLinks, customMessageTitle, customMessageText,
    customMessageIntent, customMessageIcon, customMessageActive, showCreateOptions ]);

  return (
    <WorkspaceConfigContext.Provider value={value}>
      {children}
    </WorkspaceConfigContext.Provider>
  )
}

export function useEnableNoteflow() {
  const { enableNoteflow } = useContext(WorkspaceConfigContext);
  return enableNoteflow;
}

export function useFromEmailAddress() {
  const { fromEmailAddress } = useContext(WorkspaceConfigContext);
  return fromEmailAddress;
}

export function useFromEmailSender() {
  const { fromEmailSender } = useContext(WorkspaceConfigContext);
  return fromEmailSender;
}

export function useS3BucketName() {
  const { s3BucketName } = useContext(WorkspaceConfigContext);
  return s3BucketName;
}

export function useS3BucketKey() {
  const { s3BucketKey } = useContext(WorkspaceConfigContext);
  return s3BucketKey;
}

export function useS3BucketSecret() {
  const { s3BucketSecret } = useContext(WorkspaceConfigContext);
  return s3BucketSecret;
}

export function useS3BucketRegion() {
  const { s3BucketRegion } = useContext(WorkspaceConfigContext);
  return s3BucketRegion;
}

export function useS3BucketPresignedUrlExpiration() {
  const {s3BucketPresignedUrlExpiration} = useContext(WorkspaceConfigContext);
  return s3BucketPresignedUrlExpiration;
}

export function useDataRetentionDays() {
  const { dataRetentionDays } = useContext(WorkspaceConfigContext);
  return dataRetentionDays;
}

export function useCustomLinks() {
  const { customLinks } = useContext(WorkspaceConfigContext);
  return customLinks;
}

export function useShowCreateOptions(): WorkspaceConfigShowCreateOptions {
  const {showCreateOptions} = useContext(WorkspaceConfigContext);
  return showCreateOptions || DEFAULT_SHOW_CREATE_OPTIONS;
}

export function useCustomMessageTitle() {
  const { customMessageTitle } = useContext(WorkspaceConfigContext);
  return customMessageTitle;
}

export function useCustomMessageText() {
  const { customMessageText } = useContext(WorkspaceConfigContext);
  return customMessageText;
}

export function useCustomMessageIntent() {
  const { customMessageIntent } = useContext(WorkspaceConfigContext);
  return customMessageIntent;
}

export function useCustomMessageIcon() {
  const { customMessageIcon } = useContext(WorkspaceConfigContext);
  return customMessageIcon;
}

export function useCustomMessageActive() {
  const { customMessageActive } = useContext(WorkspaceConfigContext);
  return customMessageActive;
}
